<template>
  <BaseModal
    :heading="$t(`marketing.discounts.discount-code-modal.${discountCodeId ? 'update' : 'create'}_title`)"
    small
    useForm
    :loading="loading"
    :parentRoute="parentRouteName"
    @close="close"
    @submit="submit"
  >
    <div :class="[$style.base, { [$style.smallScreen]: $screen === 's' }]">
      <Flex>
        <BaseText>
          {{ $t('marketing.discounts.discount-code-modal.description') }}
        </BaseText>
        <BaseToggle
          v-if="route.name !== 'newsletter-template'"
          v-model="enabled"
          :label="$t('global.actions.enable')"
          v-test="'discount-enable'"
        />
      </Flex>
      <div :class="$style.fields">
        <Flex
          direction="column"
          :gap=".5"
        >
          <BaseInput
            v-model="formData.code"
            label="Code"
            required
            :disabled="!!discountCodeId"
            :info="$t('marketing.discounts.discount-code-modal.discount_code_description')"
            :error="
            codeInUse
              ? $t('global.validations.in_use', {
                  value: $t('marketing.discounts.discount-code-modal.code')
                })
              : undefined
          "
            v-test="'discount-code'"
          />
          <BaseText
            v-if="!discountCodeId"
            link
            @click="generateRandomCode"
            v-test="'discount-generate-code'"
          >
            {{ $t('marketing.discounts.discount-code-modal.generate_code') }}
          </BaseText>
        </Flex>
        <BaseDropdown
          v-model="formData.category"
          :options="[
        {
          label: $t('marketing.discounts.category.holidays'),
          value: 'holidays',
        },
        {
          label: $t('marketing.discounts.category.new_customers'),
          value: 'new_customers',
        },
        {
          label: $t('marketing.discounts.category.rebook_coupons'),
          value: 'rebook_coupons',
        },
        {
          label: $t('marketing.discounts.category.special_events'),
          value: 'special_events',
        },
        {
          label: $t('marketing.discounts.category.other'),
          value: 'other',
        },
      ]"
          :label="$t('marketing.discounts.discount-code-modal.category_title')"
          v-test="'discount-category'"
        />
        <BaseDropdown
          v-if="unleash.isEnabled('GlobalDiscounts')"
          v-model="formData.discountType"
          :options="[
          {
            value: DiscountCodeDiscountTypeEnum.Percentage,
            label: $t('marketing.discounts.discount_percentage')
          },  
          {
            value: DiscountCodeDiscountTypeEnum.FixedAmount,
            label: $t('marketing.discounts.discount_fixed_amount')
          }
        ]"
          :label="$t('marketing.discounts.discount_type')"
          :disabled="!!discountCodeId"
          v-test="'discount-type'"
        />
        <BaseDropdown
          v-if="formData.discountType === DiscountCodeDiscountTypeEnum.Percentage"
          v-model="formData.discountPercentage"
          :label="$t('marketing.discounts.discount-code-modal.discount_percentage')"
          :options="[
          5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90,
          95,
        ].map((percentage) => ({
          value: percentage,
          label: `${percentage}%`,
        }))"
          :disabled="!!discountCodeId"
          v-test="'discount-percentage'"
        />
        <BaseInput
          v-if="formData.discountType === DiscountCodeDiscountTypeEnum.FixedAmount"
          v-model="formData.discountAmount"
          :label="$t('marketing.discounts.discount_fixed_amount')"
          required
          type="currency"
          :disabled="!!discountCodeId"
          v-test="'discount-amount'"
        />
      </div>
      <div :class="$style.divider" />
      <div :class="$style.fields">
        <BaseDatePicker
          v-model="formData.validFrom"
          :label="$t('marketing.discounts.discount_valid_from')"
          v-test="'discount-valid-from'"
        />
        <BaseDropdown
          v-model="formData.expiresIn"
          :options="[
          {
            label: $t(
              'marketing.discounts.discount-code-modal.expiration.never',
            ),
            value: DiscountCodeExpiresInEnum.Never,
          },
          {
            label: $t(
              'marketing.discounts.discount-code-modal.expiration.one_month',
            ),
            value: DiscountCodeExpiresInEnum.OneMonth,
          },
          {
            label: $t(
              'marketing.discounts.discount-code-modal.expiration.three_months',
            ),
            value: DiscountCodeExpiresInEnum.ThreeMonths,
          },
          {
            label: $t(
              'marketing.discounts.discount-code-modal.expiration.six_months',
            ),
            value: DiscountCodeExpiresInEnum.SixMonths,
          },
          {
            label: $t(
              'marketing.discounts.discount-code-modal.expiration.one_year',
            ),
            value: DiscountCodeExpiresInEnum.OneYear,
          },
          {
            label: $t('global.custom'),
            value: DiscountCodeExpiresInEnum.Custom,
          },
        ]"
          :label="
          $t('marketing.discounts.discount-code-modal.expiration_time_title')
        "
          @update:modelValue="onExpiresInChange"
          v-test="'discount-expires-in'"
        />
        <BaseDatePicker
          v-if="formData.expiresIn === DiscountCodeExpiresInEnum.Custom"
          v-model="formData.validUntil"
          :label="$t('marketing.discounts.discount_valid_until')"
          :minValue="formData.validFrom"
          v-test="'discount-valid-until'"
        />
      </div>
      <div :class="$style.divider" />
      <Flex direction="column">
        <BaseInputLabel
          :text="$t('marketing.discounts.discount-code-modal.usage_limits')"
        />
        <BaseCheckbox
          v-model="formData.oncePerCustomer"
          :label="
          $t('marketing.discounts.discount-code-modal.once_per_customer')
        "
          v-test="'discount-once'"
        />
        <BaseCheckbox
          v-model="formData.hasLimit"
          :label="$t('marketing.discounts.discount-code-modal.limit')"
          v-test="'discount-limit'"
        />
        <Flex>
          <BaseInput
            v-if="formData.hasLimit"
            v-model="formData.usageLimit"
            :label="$t('marketing.discounts.discount-code-modal.max_uses')"
            type="number"
            :minValue="1"
            controls
            v-test="'discount-limit-amount'"
          />
        </Flex>
        <Services
          v-if="unleash.isEnabled('ServiceDiscounts')"
          v-model="formData.serviceIds"
        />
      </Flex>
    </div>
    <template #footer>
      <BaseButton
        submitForm
        :loading="saving"
        v-test="'discount-save'"
      >
        {{ $t('global.actions.save') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import dayjs from '@/dayjs';
import eventBus from '@/event-bus';
import { flash } from '@/helpers/ui';
import { useOnboardingStore } from '@/stores/onboarding';
import {
  type CreateDiscountCodeInput,
  DiscountCodeAppliesToEnum,
  DiscountCodeDiscountTypeEnum,
  DiscountCodeExpiresInEnum,
  DiscountCodeStateEnum,
} from '@/types';
import unleash from '@/unleash';
import {
  CREATE_DISCOUNT_CODE,
  GET_DISCOUNT_CODE,
  UPDATE_DISCOUNT_CODE,
} from './graphql';
import Services from './Services.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const props = defineProps<{
  parentRouteName?: string;
}>();

const emit = defineEmits(['close', 'discountCode']);

const formData = ref<CreateDiscountCodeInput>({
  appliesTo: DiscountCodeAppliesToEnum.AllServices,
  category: 'holidays',
  code: '',
  discountAmount: 500,
  discountPercentage: 5,
  discountType: DiscountCodeDiscountTypeEnum.Percentage,
  expiresIn: DiscountCodeExpiresInEnum.Never,
  hasLimit: false,
  oncePerCustomer: false,
  serviceIds: [],
  state: DiscountCodeStateEnum.Enabled,
  usageLimit: 1,
  validFrom: dayjs().format(),
  validUntil: dayjs().add(1, 'month').format(),
});

const onExpiresInChange = (value: DiscountCodeExpiresInEnum) => {
  if (
    value === DiscountCodeExpiresInEnum.Custom &&
    !formData.value.validUntil
  ) {
    formData.value.validUntil = dayjs().add(1, 'month').format();
  } else {
    formData.value.validUntil = null;
  }
};

watch(
  () => formData.value.serviceIds?.length,
  (value) => {
    formData.value.appliesTo = value
      ? DiscountCodeAppliesToEnum.SpecificServices
      : DiscountCodeAppliesToEnum.AllServices;
  },
);

const enabled = computed({
  get() {
    return formData.value.state === DiscountCodeStateEnum.Enabled;
  },
  set(value) {
    formData.value.state = value
      ? DiscountCodeStateEnum.Enabled
      : DiscountCodeStateEnum.Disabled;
  },
});

const discountCodeId = computed(() =>
  route.params.discountId
    ? Number.parseInt(route.params.discountId as string)
    : null,
);

const loading = ref(false);
if (discountCodeId.value) {
  loading.value = true;
  const { onResult } = useQuery(GET_DISCOUNT_CODE, {
    id: discountCodeId.value,
  });
  onResult(({ data: { discountCode } }) => {
    if (!discountCode) {
      return;
    }

    Object.keys(formData.value).forEach((key) => {
      if (discountCode[key] !== undefined) {
        formData.value[key] = discountCode[key];
      }
    });

    loading.value = false;
  });
}

const codeInUse = ref(false);
watch(
  () => formData.value.code,
  () => {
    codeInUse.value = false;
  },
);

const generateRandomCode = () => {
  // Skip the 0 and O, 1 and I characters because they look too similar
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'.split('');
  const pickRandomChar = () => chars[Math.floor(Math.random() * chars.length)];
  formData.value.code = Array.from(new Array(8), pickRandomChar).join('');
};

const { mutate, loading: saving } = useMutation(
  discountCodeId.value ? UPDATE_DISCOUNT_CODE : CREATE_DISCOUNT_CODE,
  {
    refetchQueries: ['getDiscountCodes'],
  },
);

const submit = () => {
  if (codeInUse.value) {
    return;
  }

  const data = {
    ...formData.value,
  };

  if (data.discountType === DiscountCodeDiscountTypeEnum.Percentage) {
    data.discountAmount = 0;
  } else {
    data.discountPercentage = 0;
  }

  if (!data.hasLimit) {
    data.usageLimit = null;
  }

  if (data.expiresIn !== DiscountCodeExpiresInEnum.Custom) {
    data.validUntil = null;
  }

  const { code, discountPercentage, ...rest } = data;
  const input = discountCodeId.value
    ? {
        ...rest,
        id: discountCodeId.value,
      }
    : data;

  mutate({
    input,
  }).then(({ data }) => {
    if (data.createDiscountCode?.errors?.code) {
      codeInUse.value = true;
      return;
    }

    flash(
      t(
        `global.flash.${discountCodeId.value ? 'discount_code_updated' : 'discount_code_created'}`,
      ),
    );

    if (!discountCodeId.value) {
      emit('discountCode', data.createDiscountCode?.discountCode);

      const onboardingStore = useOnboardingStore();
      if (onboardingStore.currentOnboardingFlow === 'marketing') {
        eventBus.$emit('open-task-menu');
        onboardingStore.currentOnboardingFlow = '';
      }
    }

    close();
  });
};

const close = () => {
  if (props.parentRouteName) {
    router.push({ name: props.parentRouteName });
  } else {
    emit('close');
  }
};
</script>

<style
  lang="scss"
  module
>
.fields {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing;
  margin-top: $spacing;

  .base:not(.smallScreen) & {
    & > * {
      width: calc(50% - $spacing / 2);
    }
  }

  .base.smallScreen & {
    flex-direction: column;
  }
}

.divider {
  height: 1px;
  background-color: var(--color-border);
  margin: $spacing * 2 0;
}
</style>
